import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/images/logo.png";
import type { NavLink } from "../../shared/types/navigation";

type SiteHeaderProps = {
  navLinks?: NavLink[];
};

function SiteHeader({ navLinks = [] }: SiteHeaderProps) {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollingTo, setScrollingTo] = useState<string | null>(null);
  const [suppressActive, setSuppressActive] = useState(false);
  const headerBarRef = useRef<HTMLDivElement | null>(null);
  const isProfilePage = location.pathname === "/profile";

  const anchorLinks = useMemo(
    () => navLinks.filter((link) => link.href.startsWith("#")),
    [navLinks],
  );

  const getHeaderOffset = () =>
    headerBarRef.current?.getBoundingClientRect().height ?? 0;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleNavScrollEvent = (event: Event) => {
      const targetId = (event as CustomEvent<string>).detail;
      if (!targetId) return;
      setSuppressActive(false);
      setActiveSection(targetId);
      setScrollingTo(targetId);
    };

    window.addEventListener(
      "nav-scroll",
      handleNavScrollEvent as EventListener,
    );
    return () =>
      window.removeEventListener(
        "nav-scroll",
        handleNavScrollEvent as EventListener,
      );
  }, []);

  useEffect(() => {
    const handleNavClear = () => {
      setActiveSection("");
      setScrollingTo(null);
      setSuppressActive(true);
    };

    window.addEventListener("nav-clear", handleNavClear);
    return () => window.removeEventListener("nav-clear", handleNavClear);
  }, []);

  useEffect(() => {
    if (!suppressActive) return;
    const releaseOnTop = () => {
      if (window.scrollY <= 2) {
        setSuppressActive(false);
      }
    };
    window.addEventListener("scroll", releaseOnTop, { passive: true });
    return () => window.removeEventListener("scroll", releaseOnTop);
  }, [suppressActive]);

  useEffect(() => {
    if (!anchorLinks.length) return;

    let ticking = false;

    const updateActiveSection = () => {
      ticking = false;
      if (suppressActive) return;
      const headerOffset = getHeaderOffset();
      const scrollPos = window.scrollY + headerOffset;
      let current = "";

      if (scrollingTo) {
        if (activeSection !== scrollingTo) {
          setActiveSection(scrollingTo);
        }
        const target = document.querySelector(
          scrollingTo,
        ) as HTMLElement | null;
        if (target) {
          const targetTop = target.getBoundingClientRect().top + window.scrollY;
          const reached = Math.abs(scrollPos - targetTop) <= 0;
          if (reached) {
            setScrollingTo(null);
          }
          return;
        }
        setScrollingTo(null);
      }

      const firstSection = document.querySelector(
        anchorLinks[0].href,
      ) as HTMLElement | null;
      if (firstSection) {
        const firstTop =
          firstSection.getBoundingClientRect().top + window.scrollY;
        if (scrollPos < firstTop) {
          setActiveSection("");
          return;
        }
      }

      anchorLinks.forEach((link) => {
        const section = document.querySelector(link.href);
        if (section) {
          const top =
            (section as HTMLElement).getBoundingClientRect().top +
            window.scrollY;
          if (scrollPos >= top) {
            current = link.href;
          }
        }
      });

      setActiveSection(current);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateActiveSection);
    };
  }, [anchorLinks, scrollingTo, activeSection, suppressActive]);

  const handleSmoothScroll = (
    e: MouseEvent<HTMLAnchorElement>,
    targetId: string,
  ) => {
    e.preventDefault();
    setSuppressActive(false);
    setActiveSection(targetId);
    setScrollingTo(targetId);
    const element = document.querySelector(targetId);
    if (element) {
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const offset = getHeaderOffset();
      const targetPosition = elementTop - offset;
      window.scrollTo({
        behavior: "smooth",
        top: targetPosition,
      });
    }
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const updateHeaderOffsetVar = () => {
      const offset = headerBarRef.current?.getBoundingClientRect().height ?? 0;
      if (offset > 0) {
        document.documentElement.style.setProperty(
          "--header-offset",
          `${Math.round(offset)}px`,
        );
      }
    };

    updateHeaderOffsetVar();

    const target = headerBarRef.current;
    let resizeObserver: ResizeObserver | null = null;

    if (target && "ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(updateHeaderOffsetVar);
      resizeObserver.observe(target);
    } else {
      window.addEventListener("resize", updateHeaderOffsetVar, {
        passive: true,
      });
    }

    return () => {
      if (resizeObserver && target) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", updateHeaderOffsetVar);
      }
    };
  }, []);

  const renderNavLinks = (isMobile?: boolean) =>
    navLinks.map((link) => {
      const isAnchor = link.href.startsWith("#");
      const isActive = isAnchor && activeSection === link.href;
      const isProfile = link.href === "/profile";
      const accentBorder = isProfilePage ? "border-blue-500" : "border-red-500";
      const accentText = isProfilePage ? "text-blue-500" : "text-red-500";
      const anchorHover = isProfilePage
        ? "hover:border-blue-500 hover:text-blue-500"
        : "hover:border-red-500 hover:text-red-500";
      const externalHover = isProfilePage
        ? "text-gray-700 hover:bg-blue-50 hover:text-blue-500 rounded-md border-b-2 border-transparent"
        : "text-gray-700 hover:bg-red-50 hover:text-red-500 rounded-md border-b-2 border-transparent";

      const baseClass =
        "px-4 py-2 text-sm font-medium transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-1";
      const anchorClass = `${
        isActive
          ? `${accentBorder} ${accentText} border-b-2`
          : "border-transparent text-gray-700 border-b-2"
      } ${anchorHover}`;

      return (
        <a
          key={link.href}
          href={link.href}
          onClick={
            isAnchor ? (e) => handleSmoothScroll(e, link.href) : undefined
          }
          target={link.external ? "_blank" : undefined}
          rel={link.external ? "noreferrer noopener" : undefined}
          className={`${baseClass} ${
            isAnchor ? anchorClass : externalHover
          } ${isMobile ? "block" : ""}`}
        >
          <span className="inline-flex items-center gap-1">
            <span>{link.label}</span>
            {link.icon && isProfile ? (
              <i className={`${link.icon} text-[1em] text-current`}></i>
            ) : null}
          </span>
        </a>
      );
    });

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 bg-white transition-all duration-300 ${
        isScrolled ? "shadow-md" : "shadow-sm"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24">
        <div
          ref={headerBarRef}
          className="mx-auto flex h-[60px] max-w-7xl items-center justify-between md:h-auto md:py-4"
        >
          <Link
            to="/"
            onClick={(e) => {
              if (location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
              setIsMobileMenuOpen(false);
            }}
            className="cursor-pointer"
          >
            <img src={logo} alt="cocoIT" className="h-12 w-auto" />
          </Link>
          <nav className="hidden items-center gap-2 md:flex">
            {renderNavLinks()}
          </nav>
          <button
            className="flex h-10 w-10 cursor-pointer items-center justify-center text-gray-700 md:hidden"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            aria-label={
              isMobileMenuOpen ? "メニューを閉じる" : "メニューを開く"
            }
            aria-expanded={isMobileMenuOpen}
          >
            <i
              className={`${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}
            ></i>
          </button>
        </div>
        <div
          className={`overflow-hidden rounded-lg bg-white transition-all duration-300 ease-in-out md:hidden ${
            isMobileMenuOpen
              ? "pointer-events-auto my-4 max-h-[400px] translate-y-0 p-0 opacity-100"
              : "pointer-events-none my-0 max-h-0 -translate-y-2 p-0 opacity-0"
          }`}
        >
          <nav className="flex flex-col gap-3">{renderNavLinks(true)}</nav>
        </div>
      </div>
    </header>
  );
}

export default SiteHeader;
